import React, { useMemo, useEffect, Fragment, useState, } from 'react';
import { Button, Modal, ModalBody, ModalFooter, Form, } from 'reactstrap';
import { sumBy, merge, isError, chunk, isEmpty, pickBy, pick, get, keyBy, omit, mapValues, debounce, } from 'lodash';
import { useToggle, useList, } from 'react-use';
import { addDays, } from 'date-fns';
import { toast, } from 'react-toastify';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import classnames from 'classnames';
import numeral from 'numeral';

import firebase, { functions } from '../../firebase';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useFormState from '../hooks/useFormState';
import Field from '../Field';
import { getAddressByPostalCode, } from '../../util';
import { batch, getCollectionData, getDocumentData, } from '../../shared/firebase';
import { deliveryTimes, } from '../../shared/config';
import { computeInboundShipmentErrorSkus, types, fields as _fields, adminFields, boxItemFields, shippingItemFields, deliveryType, } from '../../shared/models/shippingRequest';
import { computeInventory, } from '../../shared/models/product';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import useDocumentsFetch from '../hooks/useDocumentsFetch';
import ListForm from '../ListForm';
import ShippingItemForm from '../forms/ShippingItemForm';
import MenuList from '../MenuList';
import ProgressButton from '../ProgressButton';
import ProductSelectOptionLabel from '../ProductSelectOptionLabel';
import OverlayLoading from '../OverlayLoading';

const db = firebase.firestore();
const { entries, } = Object;

export default function InboundShipmentCompleteFormModal(props) {
  const { isOpen, onClickClose, values, tenant, shippingRequest, count, products, productsById, } = props;
  const [boxes, { set: setBoxes, updateAt: updateBoxAt }] = useList(values.boxes || Array(count).fill().map(_ => ({ items: [], })));
  const [isSubmitting, toggleSubmitting] = useToggle(false);
  const computedItems = shippingRequest.items.map((item, i) => {
    const { productId, goodQuantity } = item;
    const total = sumBy(boxes, _ => sumBy(_.items?.filter(_ => _.productId === productId), 'quantity'));
    const diff = total - goodQuantity;
    return {
      ...item,
      total,
      diff,
    };
  });
  const isUnsubmittable = boxes.some(_ => !_.isValid) || computedItems.some(_ => _.diff !== 0);
  const onSubmit = async (event) => {
    event.preventDefault();
    if(isUnsubmittable) return;

    toggleSubmitting(true);
    try {
      await props.onSubmit({
        boxes: boxes.map(_ => ({ items: _.items.map(_ => ({ ..._, sku: productsById[_.productId].sku, })) })),
      });
    } catch(e) {
      toast.error('失敗しました');
      throw e;
    } finally {
      toggleSubmitting(false);
    }
  };
  const productOptions = products.map((product) => {
    const { id, ref, sku, code, name, asin, fnsku, barcodeInfo, } = product;
    return {
      value: id,
      queryLabel: [id, name, sku, code, asin, fnsku, barcodeInfo].join(' '),
      label: (
        <ProductSelectOptionLabel product={product} />
      ),
    };
  });

  return (
    <Modal className="position-relative" isOpen={isOpen} style={{ minWidth: '90vw', }}>
      <div className="modal-header">
        <div className="w-100 d-flex justify-content-between">
          <h5 className="modal-title">
            内容詳細
          </h5>
        </div>
      </div>
      <Form onSubmit={onSubmit}>
        <ModalBody>
          <div className="d-flex flex-column gap-1">
            {
              boxes.map((box, i) => {
                return (
                  <div key={i} className="card p-2">
                    <h5>箱{i+1}</h5>
                    <BoxForm
                      index={i}
                      values={box}
                      {...{
                        shippingRequest,
                        tenant,
                        products,
                        productsById,
                        productOptions,
                      }}
                      onChange={_ => updateBoxAt(i, _)}
                    />
                  </div>
                );
              })
            }
          </div>
          <hr className="my-5"/>
          <div>
            <table className="table">
              <thead>
                <tr>
                  <th>FNSKU</th>
                  <th>依頼数</th>
                  <th>入力数合計</th>
                  <th>差</th>
                </tr>
              </thead>
              <tbody>
                {
                  computedItems.map((item, i) => {
                    const { productId, goodQuantity, total, diff, } = item;
                    return (
                      <tr key={i}>
                        <td>{productsById[productId]?.fnsku}</td>
                        <td>{goodQuantity}</td>
                        <td>{total}</td>
                        <td className={classnames({ 'text-danger': Math.abs(diff) > 0 })}>{numeral(diff).format('+')}</td>
                      </tr>
                    );
                  })
                }
              </tbody>
            </table>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button className="cancel" color="secondary" onClick={onClickClose}>閉じる</Button>
          <Button className="save" type="submit" color="primary" onClick={onSubmit} disabled={isUnsubmittable || isSubmitting}>保存</Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

function BoxForm (props) {
  const { values, tenant, shippingRequest, productsById, productOptions, onChange, } = props;
  const [items, { set: setItems, updateAt: updateItemAt }] = useList(values.items || []);
  useEffect(() => {
    onChange({
      items,
      isValid: items.every(_ => _.isValid),
    });
  }, [items]);

  return (
    <div>
      <ListForm
        initialItem={_ => ({ quantity: 1, })}
        addButtonLabel="商品追加"
        items={items}
        renderItem={(item, itemIndex) => {
          return (
            <div className="card p-3">
              <ItemForm
                fields={boxItemFields({ shippingRequest, })}
                index={itemIndex}
                values={item}
                tenant={tenant}
                shippingRequest={shippingRequest}
                productOptions={productOptions}
                onChange={_ => updateItemAt(itemIndex, { ...item, ..._, })}
              />
            </div>
          );
        }}
        onChange={setItems}
      />
    </div>
  );
}

function ItemForm (props) {
  const { index: itemIndex, fields, values, tenant, shippingRequest, onChange, productOptions, disabled = false, } = props;
  const statedFields = useFormState(values, fields, values);
  useEffect(() => {
    onChange({
      ...mapValues(statedFields, 'value'),
      isValid: Object.values(statedFields).every(_ => _.isValid),
    });
  }, [...Object.values(statedFields).map(_ => _.value), ...Object.values(statedFields).map(_ => _.isValid)]);

  return (
    <div>
      <div className="d-flex gap-1">
        <div style={{ minWidth: 230, }}>
          <Field
            name="productId"
            {...statedFields.productId}
            {...{
              options: productOptions,
              inputProps: {
                styles: { 
                  valueContainer: _ => ({ ..._, height: 60 }),
                  singleValue: _ => ({ ..._, top: 0, transform: '' }),
                },
              },
            }}
          />
        </div>
        <div style={{ width: 65, }}>
          <Field
            name="quantity"
            {...statedFields.quantity}
          />
        </div>
      </div>
    </div>
  );
}
